import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { useI18n } from "../lib/i18n";
import { useSite } from "../lib/site";
import { useConsent } from "../lib/store";

const XIcon = (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M18.9 2H22l-7.3 8.3L23 22h-6.6l-5.2-6.8L5.3 22H2.2l7.8-8.9L1.5 2h6.7l4.7 6.2zm-1.1 18h1.7L7.3 3.9H5.5z" /></svg>);
const SnapIcon = (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12 2c3 0 5.5 2.2 5.5 5.6v2.6c.6.2 1.4-.3 1.8 0 .5.4 0 1-.9 1.4-.6.3-1.3.5-1.2 1 .4 1.6 2 3.2 3.5 3.6.4.1.4.6-.1.8-.8.4-1.9.4-2.2.7-.3.4-.2 1.1-.6 1.2-.5.2-1.4-.3-2.6 0-1.3.3-2 1.7-3.2 1.7s-1.9-1.4-3.2-1.7c-1.2-.3-2.1.2-2.6 0-.4-.1-.3-.8-.6-1.2-.3-.3-1.4-.3-2.2-.7-.5-.2-.5-.7-.1-.8 1.5-.4 3.1-2 3.5-3.6.1-.5-.6-.7-1.2-1-.9-.4-1.4-1-.9-1.4.4-.3 1.2.2 1.8 0V7.6C6.5 4.2 9 2 12 2z" /></svg>);
const SOCIAL_ICONS = { instagram: Instagram, x: XIcon, linkedin: Linkedin, snapchat: SnapIcon };

const COLS = [
  ["footer.explore", [["/properties", "nav.allProperties"], ["/properties/for-sale", "nav.forSale"], ["/properties/for-rent", "nav.forRent"], ["/developments", "nav.developments"], ["/investment", "nav.investment"]]],
  ["footer.company", [["/about", "nav.aboutUs"], ["/vision-mission", "nav.vision"], ["/services", "nav.services"], ["/news", "nav.news"], ["/contact", "nav.contact"]]],
];

const fmtDigits = (v, lang) => (lang === "ar" ? String(v).replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[d]) : String(v));

export function Footer() {
  const { t, tr, lang } = useI18n();
  const { settings } = useSite();
  const [, setConsent] = useConsent();
  const ph = settings?.placeholders || {};
  return (
    <footer data-testid="site-footer" className="on-media relative overflow-hidden bg-surface">
      <div className="divider-brand" aria-hidden="true" />
      <div className="mashrabiya pointer-events-none absolute inset-0 opacity-[0.04]" aria-hidden="true" />
      <div className="container-lux relative grid gap-12 py-20 md:grid-cols-12">
        <div className="md:col-span-4">
          <Logo variant="full" />
          <p className="mt-6 max-w-sm font-display text-xl leading-snug text-fg/85">{tr(settings, "tagline")}</p>
        </div>
        {COLS.map(([title, links]) => (
          <div key={title} className="md:col-span-2">
            <h3 className="eyebrow mb-5">{t(title)}</h3>
            <ul className="space-y-3">
              {links.map(([to, key]) => (<li key={to}><Link to={to} className="text-sm text-fg/70 transition-colors hover:text-cta">{t(key)}</Link></li>))}
            </ul>
          </div>
        ))}
        <div className="md:col-span-4">
          <h3 className="eyebrow mb-5">{t("nav.contact")}</h3>
          {settings && (
            <ul className="space-y-4 text-sm text-fg/75">
              {!ph.phone && <li className="flex items-start gap-3"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-cta" />
                <span className="flex flex-col gap-1">
                  {[settings.phone, ...(settings.mobiles || [])].filter(Boolean).map((n, k) => (<a key={n} href={`tel:${n.replace(/\s/g, "")}`} dir="ltr" data-testid={k ? `footer-mobile-${k}` : "footer-phone"} className="hover:text-cta">{n}</a>))}
                </span></li>}
              {!ph.email && <li className="flex items-start gap-3"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-cta" /><a href={`mailto:${settings.email}`} data-testid="footer-email" className="hover:text-cta">{settings.email}</a></li>}
              {!ph.address && <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cta" /><span data-testid="footer-address">{tr(settings, "address")}</span></li>}
            </ul>
          )}
          <div className="mt-6 flex items-center gap-3" aria-label={t("footer.follow")}>
            {Object.entries(settings?.socials || {}).map(([k, url]) => {
              const Icon = SOCIAL_ICONS[k];
              return Icon && url && !ph.socials ? (
                <a key={k} href={url} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${k}`} aria-label={k} className="flex h-9 w-9 items-center justify-center rounded-full border border-fg/15 text-fg/70 transition-colors hover:border-cta hover:text-cta"><Icon className="h-4 w-4" /></a>
              ) : null;
            })}
          </div>
        </div>
      </div>
      <div className="relative border-t border-fg/5">
        <div className="container-lux flex flex-col gap-4 py-6 text-xs text-fg/50 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} {tr(settings, "company_name")}. {t("footer.rights")}{settings?.fal_licence_no && !ph.fal_licence_no && <> · <span data-testid="footer-fal-licence">{t("footer.fal")}: {settings.fal_licence_no}</span></>}
            {settings?.cr_number && <> · <span data-testid="footer-cr">{lang === "ar" ? `س.ت ${fmtDigits(settings.cr_number, lang)}` : `C.R. ${settings.cr_number}`}</span></>}
            {settings?.vat_number && <> · <span data-testid="footer-vat">{lang === "ar" ? `الرقم الضريبي ${fmtDigits(settings.vat_number, lang)}` : `VAT No. ${settings.vat_number}`}</span></>}</p>
          <div className="flex flex-wrap items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-cta">{t("footer.privacy")}</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-cta">{t("footer.terms")}</Link>
            <button type="button" data-testid="footer-cookie-settings" onClick={() => setConsent(null)} className="hover:text-cta">{t("footer.cookies")}</button>
            <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-cta">{t("footer.admin")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
